import { useState } from 'react';
import ModalFrame from './modals/ModalFrame';
import { ThemeButton } from './ThemeWrapper';
import { labFleetAPI } from '../services/api';

function formatBytes(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n >= 1024 * 1024 * 1024) return `${(n / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
}

function errorMessage(err, fallback) {
  return err?.response?.data?.message || err?.message || fallback;
}

export default function HomeBackupsButton({ disabled = false, onChanged }) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(null);
  const [backups, setBackups] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await labFleetAPI.homeBackups();
      setBackups(Array.isArray(res.data?.backups) ? res.data.backups : []);
      setHosts(Array.isArray(res.data?.hosts) ? res.data.hosts : []);
    } catch (err) {
      setError(errorMessage(err, 'Could not read home backups'));
    } finally {
      setLoading(false);
    }
  };

  const openList = () => {
    setOpen(true);
    setPending(null);
    load();
  };

  const closeList = () => {
    if (removing || pending) return;
    setOpen(false);
  };

  const confirmRemove = async () => {
    if (!pending) return;
    setRemoving(true);
    setError('');
    try {
      await labFleetAPI.removeHomeBackup({ alias: pending.alias, path: pending.path });
      setPending(null);
      await load();
      if (typeof onChanged === 'function') onChanged();
    } catch (err) {
      setError(errorMessage(err, 'The backup was not removed'));
      setPending(null);
    } finally {
      setRemoving(false);
    }
  };

  const failedHosts = hosts.filter((host) => host.ok === false);

  return (
    <>
      <ThemeButton type="button" onClick={openList} disabled={disabled}>
        Home backups
      </ThemeButton>
      <ModalFrame
        isOpen={open}
        onClose={closeList}
        title="Home backups"
        description="Archives saved on the lab servers before a local account was removed. Removing an archive does not delete the student home."
        maxWidthClass="max-w-3xl"
      >
        {loading ? <p className="text-sm text-gray-600">Reading backups…</p> : null}
        {error ? (
          <p className="text-sm mb-3" style={{ color: '#b91c1c' }} role="alert">
            {error}
          </p>
        ) : null}
        {failedHosts.length ? (
          <ul className="text-sm mb-3 space-y-1">
            {failedHosts.map((host) => (
              <li key={host.alias} style={{ color: '#b91c1c' }}>
                {host.alias}: {host.error}
              </li>
            ))}
          </ul>
        ) : null}
        {!loading && !backups.length ? <p className="text-sm text-gray-600">No home backups on these servers.</p> : null}
        {backups.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500">
                  <th className="pr-3 py-2">Server</th>
                  <th className="pr-3 py-2">Account</th>
                  <th className="pr-3 py-2">Saved</th>
                  <th className="pr-3 py-2">Size</th>
                  <th className="pr-3 py-2">File</th>
                  <th className="py-2"> </th>
                </tr>
              </thead>
              <tbody>
                {backups.map((row) => (
                  <tr key={`${row.alias}-${row.path}`} style={{ borderTop: '1px solid #e5e7eb' }}>
                    <td className="pr-3 py-2 align-top">{row.alias}</td>
                    <td className="pr-3 py-2 align-top font-mono text-xs">{row.username}</td>
                    <td className="pr-3 py-2 align-top whitespace-nowrap">{row.createdAt}</td>
                    <td className="pr-3 py-2 align-top whitespace-nowrap">
                      {formatBytes(row.bytes)}
                      {row.kind === 'partial' ? <div className="text-xs text-gray-500">Incomplete</div> : null}
                    </td>
                    <td className="pr-3 py-2 align-top font-mono text-xs break-all">{row.path}</td>
                    <td className="py-2 align-top">
                      <ThemeButton type="button" variant="secondary" onClick={() => setPending(row)} disabled={removing}>
                        Remove
                      </ThemeButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </ModalFrame>
      <ModalFrame
        isOpen={Boolean(pending)}
        onClose={() => {
          if (!removing) setPending(null);
        }}
        title="Remove this backup?"
        description="The archive file will be deleted from the server. The student home and the Linux account stay."
      >
        {pending ? (
          <p className="font-mono text-xs break-all text-gray-800">
            {pending.alias}: {pending.path}
          </p>
        ) : null}
        <div className="mt-4 flex justify-end gap-2">
          <ThemeButton type="button" variant="secondary" onClick={() => setPending(null)} disabled={removing}>
            Cancel
          </ThemeButton>
          <ThemeButton type="button" onClick={confirmRemove} disabled={removing}>
            {removing ? 'Removing…' : 'Remove backup'}
          </ThemeButton>
        </div>
      </ModalFrame>
    </>
  );
}
